import { inject, Injectable } from '@angular/core';
import { SubscriptionDto } from '@/shared/openapi/model/subscription-dto';
import { UpdateSubscriptionDto } from '@/shared/openapi/model/update-subscription-dto';
import { SubscriptionsService as SubscriptionServiceAPI } from '@/shared/openapi/api/subscriptions.service';
import { map, Observable, switchMap, catchError } from 'rxjs';
import { CreateSubscriptionDto } from '@/shared/openapi/model/create-subscription-dto';
import { SubscriptionCheckResponseDto } from '@/shared/openapi/model/subscription-check-response-dto';
import { SubscriptionUsageResponseDto } from '@/shared/openapi/model/subscription-usage-response-dto';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionsService {
  private readonly _subscriptionServiceAPI = inject(SubscriptionServiceAPI);

  getUsage(): Observable<SubscriptionUsageResponseDto> {
    return this._subscriptionServiceAPI.subscriptionControllerUsage();
  }

  checkSubscription(): Observable<SubscriptionCheckResponseDto> {
    return this._subscriptionServiceAPI.subscriptionControllerCheck();
  }

  getActiveSubscription(organizationId: string): Observable<SubscriptionDto> {
    return this._subscriptionServiceAPI
      .subscriptionControllerFindByOrganizationId({
        organizationId,
      })
      .pipe(map(response => response));
  }

  createSubscription(
    organizationId: string,
    subscriptionData: CreateSubscriptionDto
  ): Observable<SubscriptionDto> {
    const createSubscriptionBody: CreateSubscriptionDto = {
      organization_id: organizationId,
      ...subscriptionData,
    };

    return this._subscriptionServiceAPI.subscriptionControllerCreateSubscription(
      {
        createSubscriptionDto: createSubscriptionBody,
      }
    );
  }

  updateSubscription(
    organizationId: string,
    free_until?: number,
    pricing_plan_id?: string
  ): Observable<SubscriptionDto> {
    return this.getActiveSubscription(organizationId).pipe(
      switchMap(activeSubscription => {
        // Check if pricing plan is changing
        const isPricingPlanChanging =
          pricing_plan_id &&
          pricing_plan_id !== activeSubscription.pricing_plan_id;

        if (isPricingPlanChanging) {
          // Create new subscription with new pricing plan
          const createSubscriptionBody: CreateSubscriptionDto = {
            organization_id: organizationId,
            pricing_plan_id: pricing_plan_id,
            free_until: free_until,
          };

          return this._subscriptionServiceAPI.subscriptionControllerCreateSubscription(
            {
              createSubscriptionDto: createSubscriptionBody,
            }
          );
        } else {
          // Update existing subscription
          const updateSubscriptionBody: UpdateSubscriptionDto = {
            organization_id: organizationId,
          };

          if (free_until) {
            updateSubscriptionBody.paid_until = free_until;
          }

          return this._subscriptionServiceAPI.subscriptionControllerUpdateSubscription(
            {
              updateSubscriptionDto: updateSubscriptionBody,
            }
          );
        }
      }),
      catchError(error => {
        console.error('Error updating subscription:', error);
        throw error;
      })
    );
  }
}
